import { createContext, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { Modal } from "~/components/Modal";
import { getDemoEvaluation } from "~/modules/evalutation/services";
import type { Evaluation } from "~/modules/evalutation/types";

import { AerobicCapabilitiesForm } from "./components/formsContainer/components/aerobicCapabitiliesForm";
import { AerobicExercises } from "./components/formsContainer/components/aerobicExercises";
import { AerobicPrescription } from "./components/formsContainer/components/aerobicPrescription";
import { AnamnesisForm } from "./components/formsContainer/components/anamnesisForm";
import { AntropometryForm } from "./components/formsContainer/components/antropometryForm";
import { CorporalEvaluationForm } from "./components/formsContainer/components/corporalEvaluationForm";
import { ExercisesPrescription } from "./components/formsContainer/components/exercisesPrescription";
import { HemodynamicMeasuresForm } from "./components/formsContainer/components/hemodynamicMeasuresForm";
import { Macrocycles } from "./components/formsContainer/components/macrocycles";
import { SeriesEdit } from "./components/formsContainer/components/SeriesEdit";
import { Scheduler } from "./components/formsContainer/components/scheduler";
import { FormsLayout } from "./layout";

type StudentEvaluationDemoContextData = { data: Evaluation };

export const studentEvaluationDemoContext =
	createContext<StudentEvaluationDemoContextData>(
		{} as StudentEvaluationDemoContextData,
	);

export const StudentEvaluationDemo: React.FC = () => {
	const { state } = useLocation();
	const [evaluation, setEvaluation] = useState(() => state?.data);

	useEffect(() => {
		const abortController = new AbortController();
		if (!evaluation?.id && !state?.data) {
			getDemoEvaluation(abortController).then((data) => {
				data && setEvaluation(data);
			});
		}

		return () => {
			abortController.abort();
		};
	}, [evaluation, state]);

	return !evaluation?.id ? (
		<Modal visible>
			<div className="flex flex-col flex-1 gap-4">
				<p className="text-center">Carregando dados da avaliação...</p>
				<div className="h-8 w-8 border-4 border-x-slate-200 border-y-slate-200 border-t-slate-400 rounded-full mx-auto animate-spin" />
			</div>
		</Modal>
	) : (
		<studentEvaluationDemoContext.Provider value={{ data: evaluation }}>
			<Routes>
				<Route
					path="/"
					element={
						<FormsLayout>
							<Outlet />
						</FormsLayout>
					}
				>
					<Route path="anamnese" element={<AnamnesisForm />} />
					<Route path="antropometria" element={<AntropometryForm />} />
					<Route
						path="avaliacao_corporal"
						element={<CorporalEvaluationForm />}
					/>
					<Route
						path="medidas_hemodinamicas"
						element={<HemodynamicMeasuresForm />}
					/>
					<Route
						path="capacidade_aerobica"
						element={<AerobicCapabilitiesForm />}
					/>
					<Route path="prescricao_aerobica" element={<AerobicPrescription />} />
					<Route path="agendamento" element={<Scheduler />} />
					<Route path="exercicios_aerobicos" element={<AerobicExercises />} />
					<Route
						path="prescricao_de_exercicios"
						element={<ExercisesPrescription />}
					/>
					<Route path="personalizacao_da_serie" element={<SeriesEdit />} />

					<Route path="macrociclos" element={<Macrocycles />} />

					<Route path="/" element={<Navigate to={"anamnese"} replace />} />
				</Route>
			</Routes>
		</studentEvaluationDemoContext.Provider>
	);
};
